"use client";

import { useEffect, useRef } from "react";
import useBackendUrl from "@/app/helpers/useBackendUrl";
import { useAuth } from "@/context/ContextProvider";
import {
  hasUnsafeVolatilePhotoUploads,
  initializePhotoUploadRegistry,
  usePhotoUploadSnapshot,
  usePhotoUploadRegistryStatus,
} from "@/app/[id]/profil/fotos/fotos-schiessen/layout/photoUploadRegistry";
import { showToast } from "@/app/helpers/showToast";
import { reportUploadIncidents } from "@/app/helpers/uploadIncidentTracking";

export default function PhotoUploadQueueManager() {
  const apiBaseUrl = useBackendUrl();
  const { userData, loading, authResolved } = useAuth();
  const uploads = usePhotoUploadSnapshot();
  const registryStatus = usePhotoUploadRegistryStatus();
  const hasVolatileUpload = hasUnsafeVolatilePhotoUploads(uploads);
  const notifiedFailures = useRef(new Set());

  useEffect(() => {
    if (loading || !authResolved) return;
    void initializePhotoUploadRegistry({
      apiBaseUrl,
      ownerEmail: userData?.email || null,
    });
  }, [apiBaseUrl, authResolved, loading, userData?.email]);

  useEffect(() => {
    if (loading || !authResolved) return;
    reportUploadIncidents({
      apiBaseUrl,
      uploads,
      registryStatus,
      ownerEmail: userData?.email || null,
      flow: "photo",
    });
  }, [
    apiBaseUrl,
    authResolved,
    loading,
    registryStatus,
    uploads,
    userData?.email,
  ]);

  useEffect(() => {
    if (
      loading ||
      !authResolved ||
      registryStatus.persistenceAvailable
    ) {
      return undefined;
    }
    const retry = setInterval(() => {
      void initializePhotoUploadRegistry({
        apiBaseUrl,
        ownerEmail: userData?.email || null,
      });
    }, 10_000);
    return () => clearInterval(retry);
  }, [
    apiBaseUrl,
    authResolved,
    loading,
    registryStatus.persistenceAvailable,
    userData?.email,
  ]);

  useEffect(() => {
    if (!hasVolatileUpload) return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [hasVolatileUpload]);

  useEffect(() => {
    Object.values(uploads).forEach((entry) => {
      if (entry?.state !== "failed") return;
      const notificationKey = `${entry.key}:${entry.uploadId}`;
      if (notifiedFailures.current.has(notificationKey)) return;
      notifiedFailures.current.add(notificationKey);
      showToast(
        `Das Foto „${entry.step}“ konnte nicht hochgeladen werden. Bitte lösche es oder nimm es erneut auf.`,
        "error"
      );
    });
  }, [uploads]);

  return null;
}
